/**
 * What can actually be read from a file somebody attaches, and what cannot.
 *
 * **"Any file" is the ask; "any file we can honestly read" is the promise.** A
 * model reads images, PDFs and text. It cannot read a `.docx`, a `.sketch` or a
 * zip, and accepting one to quietly ignore it is worse than refusing it —
 * somebody attaches their brand guidelines, gets a design that ignores them,
 * and concludes the feature does not work.
 *
 * So every file is classified, and a file that cannot be read comes back with
 * the reason and what to do instead. Exporting a Word document as a PDF takes
 * one keystroke and is the whole fix.
 */
/**
 * Images the model accepts. Deliberately not "anything beginning `image/`":
 * `image/svg+xml` is a document that can carry script, and `image/heic` is a
 * format it cannot decode — both would arrive looking accepted and do nothing.
 */
const IMAGES = new Set(['image/png', 'image/jpeg', 'image/gif', 'image/webp']);
/** Read as characters, whatever the extension claims. */
const TEXTS = new Set([
    'text/plain',
    'text/html',
    'text/css',
    'text/markdown',
    'text/csv',
    'application/json',
    'application/xml',
    'text/xml',
]);
/** What to say about a file that cannot be read, and what to do about it. */
const INSTEAD = {
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'export it as a PDF and attach that',
    'application/msword': 'export it as a PDF and attach that',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'export it as a PDF and attach that',
    'application/vnd.ms-powerpoint': 'export it as a PDF and attach that',
    'image/svg+xml': 'export it as a PNG and attach that',
    'image/heic': 'export it as a JPEG or PNG and attach that',
    'image/heif': 'export it as a JPEG or PNG and attach that',
    'application/zip': 'attach the files themselves rather than an archive',
    'application/x-zip-compressed': 'attach the files themselves rather than an archive',
};
/**
 * What kind of thing this is, or why it cannot be read.
 *
 * The media type, not the extension: a browser supplies the type, and a file
 * renamed `.png` is still whatever it was. A type nobody listed is refused
 * rather than guessed at — a wrong guess reaches the model as bytes it cannot
 * decode, and the error that comes back names neither the file nor the reason.
 */
export function readingOf(mediaType) {
    const type = mediaType.split(';')[0]?.trim().toLowerCase() ?? '';
    if (IMAGES.has(type))
        return { kind: 'image' };
    if (type === 'application/pdf')
        return { kind: 'document' };
    if (TEXTS.has(type))
        return { kind: 'text' };
    const instead = INSTEAD[type];
    return {
        kind: undefined,
        because: instead === undefined
            ? `${type === '' ? 'that kind of file' : type} cannot be read — attach an image, a PDF, or a text file`
            : `${type} cannot be read — ${instead}`,
    };
}
//# sourceMappingURL=designReferenceKind.js.map